import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, ClipboardList, Pencil, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { KpiCard, Panel, DataTable, type DataColumn } from "@/components/admin/analytics/AnalyticsKit";
import {
  FieldGroup,
  NotesField,
  NumberField,
  SelectField,
  TextField,
} from "@/components/admin/delivery/form-bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  deleteShiftAssignment,
  listDeliveryPartners,
  listDeliveryVehicles,
  listDeliveryZones,
  listShiftAssignments,
  saveShiftAssignment,
} from "@/lib/admin/delivery.functions";
import {
  SHIFTS,
  SHIFT_LABEL,
  shiftAssignmentSchema,
  type ShiftAssignment,
  type ShiftAssignmentInput,
} from "@/lib/admin/delivery.schemas";
import { istToday } from "@/lib/admin/production.schemas";

export const Route = createFileRoute("/_admin/admin/shift-planning")({
  head: () => ({
    meta: [
      { title: "Shift Planning — Admin" },
      {
        name: "description",
        content:
          "Plan which delivery partner works which shift, on which vehicle and in which zone, for any date.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ShiftPlanningPage,
});

const SHIFT_TIMES: Record<string, { start: string; end: string }> = {
  morning: { start: "07:00", end: "13:00" },
  afternoon: { start: "12:00", end: "18:00" },
  evening: { start: "17:00", end: "23:00" },
  night: { start: "22:00", end: "06:00" },
};

function emptyForm(date: string): ShiftAssignmentInput {
  return {
    shift_date: date,
    shift: "morning",
    partner_id: "",
    vehicle_id: null,
    zone_id: null,
    max_orders: 25,
    start_time: SHIFT_TIMES["morning"]!.start,
    end_time: SHIFT_TIMES["morning"]!.end,
    notes: "",
  };
}

function ShiftPlanningPage() {
  const queryClient = useQueryClient();
  const [day, setDay] = useState(istToday());

  const listFn = useServerFn(listShiftAssignments);
  const partnersFn = useServerFn(listDeliveryPartners);
  const vehiclesFn = useServerFn(listDeliveryVehicles);
  const zonesFn = useServerFn(listDeliveryZones);
  const saveFn = useServerFn(saveShiftAssignment);
  const deleteFn = useServerFn(deleteShiftAssignment);

  const shifts = useQuery({
    queryKey: ["admin", "delivery", "shifts", day],
    queryFn: () => listFn({ data: { date: day } as never }),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });
  const partners = useQuery({
    queryKey: ["admin", "delivery", "partners"],
    queryFn: () => partnersFn(),
    staleTime: 60_000,
  });
  const vehicles = useQuery({
    queryKey: ["admin", "delivery", "vehicles"],
    queryFn: () => vehiclesFn(),
    staleTime: 60_000,
  });
  const zones = useQuery({
    queryKey: ["admin", "delivery", "zones"],
    queryFn: () => zonesFn(),
    staleTime: 60_000,
  });

  const [editing, setEditing] = useState<ShiftAssignment | "new" | null>(null);
  const [form, setForm] = useState<ShiftAssignmentInput>(emptyForm(day));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<ShiftAssignment | null>(null);

  useEffect(() => {
    if (editing === "new") setForm(emptyForm(day));
    else if (editing)
      setForm({
        shift_date: editing.shift_date,
        shift: editing.shift,
        partner_id: editing.partner_id,
        vehicle_id: editing.vehicle_id,
        zone_id: editing.zone_id,
        max_orders: editing.max_orders,
        start_time: editing.start_time,
        end_time: editing.end_time,
        notes: editing.notes,
      });
    setErrors({});
  }, [editing, day]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "delivery"] });

  const save = useMutation({
    mutationFn: (v: { id: string | null; patch: ShiftAssignmentInput }) =>
      saveFn({ data: v as never }),
    onSuccess: async () => {
      toast.success("Shift saved");
      setEditing(null);
      await invalidate();
    },
    onError: (e: Error) =>
      toast.error(
        e.message === "duplicate"
          ? "That driver is already scheduled for this shift."
          : "Couldn't save the shift.",
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Shift removed");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: () => toast.error("Couldn't remove the shift."),
  });

  const rows = useMemo(() => shifts.data ?? [], [shifts.data]);
  const kpi = useMemo(() => {
    const scheduled = rows.length;
    const byShift = (k: string) => rows.filter((r) => r.shift === k).length;
    const capacity = rows.reduce((s, r) => s + r.max_orders, 0);
    const unstaffed = (partners.data ?? []).filter(
      (p) => p.is_active && !rows.some((r) => r.partner_id === p.id),
    ).length;
    return {
      scheduled,
      morning: byShift("morning"),
      afternoon: byShift("afternoon"),
      evening: byShift("evening"),
      capacity,
      unstaffed,
    };
  }, [rows, partners.data]);

  const partnerOptions = (partners.data ?? [])
    .filter((p) => p.is_active)
    .map((p) => ({ value: p.id, label: `${p.full_name} (${p.employee_code})` }));
  const vehicleOptions = [
    { value: "none", label: "No vehicle" },
    ...(vehicles.data ?? [])
      .filter((v) => v.status !== "inactive")
      .map((v) => ({ value: v.id, label: `${v.vehicle_number} · ${v.vehicle_name}` })),
  ];
  const zoneOptions = [
    { value: "none", label: "No zone" },
    ...(zones.data ?? []).filter((z) => z.is_active).map((z) => ({ value: z.id, label: z.zone_name })),
  ];

  const columns: Array<DataColumn<ShiftAssignment>> = [
    {
      key: "driver",
      header: "Delivery partner",
      render: (r) => <span className="font-medium text-foreground">{r.partner_name || "—"}</span>,
      sortValue: (r) => r.partner_name,
      exportValue: (r) => r.partner_name,
    },
    {
      key: "shift",
      header: "Shift",
      render: (r) => <Badge variant="secondary">{SHIFT_LABEL[r.shift]}</Badge>,
      sortValue: (r) => r.shift,
      exportValue: (r) => SHIFT_LABEL[r.shift],
    },
    {
      key: "time",
      header: "Timing",
      render: (r) => `${r.start_time} – ${r.end_time}`,
      sortValue: (r) => r.start_time,
      exportValue: (r) => `${r.start_time}-${r.end_time}`,
    },
    {
      key: "vehicle",
      header: "Vehicle",
      render: (r) => r.vehicle_number || "—",
      exportValue: (r) => r.vehicle_number,
    },
    {
      key: "zone",
      header: "Zone",
      render: (r) => r.zone_name || "—",
      sortValue: (r) => r.zone_name,
      exportValue: (r) => r.zone_name,
    },
    {
      key: "max",
      header: "Max orders",
      align: "right",
      render: (r) => r.max_orders,
      sortValue: (r) => r.max_orders,
      exportValue: (r) => r.max_orders,
    },
    {
      key: "notes",
      header: "Notes",
      render: (r) => <span className="text-xs text-muted-foreground">{r.notes || "—"}</span>,
      exportValue: (r) => r.notes,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={() => setEditing(r)}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      ),
      exportValue: () => "",
    },
  ];

  const set = <K extends keyof ShiftAssignmentInput>(key: K, value: ShiftAssignmentInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = () => {
    const parsed = shiftAssignmentSchema.safeParse(form);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] = issue.message;
      setErrors(next);
      toast.error(next["partner_id"] ? "Select a delivery partner." : "Please fix the form.");
      return;
    }
    save.mutate({
      id: editing === "new" || editing === null ? null : editing.id,
      patch: parsed.data,
    });
  };

  return (
    <div>
      <AdminPageHeader
        title="Shift Planning"
        description="Schedule drivers, vehicles and zones for each shift. Dispatch prioritises drivers scheduled for today."
      />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiCard label="Scheduled today" value={String(kpi.scheduled)} tone="info" icon={ClipboardList} loading={shifts.isLoading} />
        <KpiCard label="Morning" value={String(kpi.morning)} tone="success" icon={CalendarDays} loading={shifts.isLoading} />
        <KpiCard label="Afternoon" value={String(kpi.afternoon)} tone="teal" icon={CalendarDays} loading={shifts.isLoading} />
        <KpiCard label="Evening" value={String(kpi.evening)} tone="purple" icon={CalendarDays} loading={shifts.isLoading} />
        <KpiCard label="Planned capacity" value={String(kpi.capacity)} tone="neutral" icon={ClipboardList} loading={shifts.isLoading} />
        <KpiCard label="Unscheduled drivers" value={String(kpi.unstaffed)} tone="warn" icon={Users} loading={shifts.isLoading} />
      </div>

      <Panel title="Shift roster" subtitle={`${rows.length} assignments`}>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <Input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value || istToday())}
            className="h-9 w-44"
            aria-label="Shift date"
          />
          <Button size="sm" className="gap-1.5" onClick={() => setEditing("new")}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Assign shift
          </Button>
        </div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={shifts.isLoading}
          searchOn={(r) => `${r.partner_name} ${r.vehicle_number} ${r.zone_name} ${r.shift}`}
          pageSize={10}
          exportName={`shift-plan-${day}`}
          importModule="shift-plans"
          minWidth={1000}
        />
      </Panel>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Assign shift" : "Edit shift"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <FieldGroup title="Shift">
              <TextField
                label="Date"
                type="date"
                value={form.shift_date}
                onChange={(v) => set("shift_date", v || day)}
              />
              <SelectField
                label="Shift"
                value={form.shift}
                onChange={(v) => {
                  const times = SHIFT_TIMES[v]!;
                  setForm((f) => ({
                    ...f,
                    shift: v as ShiftAssignmentInput["shift"],
                    start_time: times.start,
                    end_time: times.end,
                  }));
                }}
                options={SHIFTS.map((s) => ({ value: s, label: SHIFT_LABEL[s] }))}
              />
              <SelectField
                label="Delivery partner"
                value={form.partner_id || ""}
                onChange={(v) => set("partner_id", v)}
                options={partnerOptions}
                placeholder="Select a driver"
              />
              <SelectField
                label="Vehicle"
                value={form.vehicle_id ?? "none"}
                onChange={(v) => set("vehicle_id", v === "none" ? null : v)}
                options={vehicleOptions}
              />
              <SelectField
                label="Zone"
                value={form.zone_id ?? "none"}
                onChange={(v) => set("zone_id", v === "none" ? null : v)}
                options={zoneOptions}
              />
              <NumberField
                label="Max orders"
                value={form.max_orders}
                onChange={(v) => set("max_orders", Math.max(0, Math.round(v)))}
              />
              <TextField
                label="Start time"
                type="time"
                value={form.start_time}
                onChange={(v) => set("start_time", v)}
              />
              <TextField
                label="End time"
                type="time"
                value={form.end_time}
                onChange={(v) => set("end_time", v)}
              />
              <NotesField value={form.notes} onChange={(v) => set("notes", v)} />
            </FieldGroup>
            {errors["partner_id"] ? (
              <p className="text-xs text-destructive">A delivery partner is required.</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove shift assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.partner_name} will no longer be scheduled for this shift.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
